import { useCallback, useEffect, useRef, useState } from 'react';

import { Event } from 'events/_types';
import { UNSEEN_UPDATE_FADE_MS } from 'events/styles/EventCard.style';

const EMPTY_IDS: ReadonlySet<string> = new Set<string>();

interface NoteIdentity {
  created: string;
  user: { id: string };
}

type EventIdentity = Pick<Event, 'id' | 'modified'> & {
  shift_notes?: NoteIdentity[];
};

function isLatestNoteFrom(
  event: EventIdentity,
  userId: string | undefined
): boolean {
  if (!userId) return false;

  const notes = event.shift_notes ?? [];
  if (notes.length === 0) return false;

  const latestNote = notes.reduce((latest, note) =>
    note.created > latest.created ? note : latest
  );
  return latestNote.user?.id === userId;
}

export interface UnseenTrackingState {
  knownModifiedById: Map<string, string>;
  primedViewKeys: Set<string>;
}

export function createUnseenTrackingState(): UnseenTrackingState {
  return { knownModifiedById: new Map(), primedViewKeys: new Set() };
}

export function collectUnseenEventIds(
  events: readonly EventIdentity[],
  state: UnseenTrackingState,
  viewKey: string,
  isLoaded: boolean,
  currentUserId?: string
): string[] {
  if (!isLoaded) return [];

  const { knownModifiedById, primedViewKeys } = state;
  const isPrimed = primedViewKeys.has(viewKey);
  primedViewKeys.add(viewKey);

  const unseen: string[] = [];
  events.forEach((event) => {
    const knownModified = knownModifiedById.get(event.id);
    if (knownModified === event.modified) return;

    knownModifiedById.set(event.id, event.modified);
    if (knownModified === undefined && !isPrimed) return;
    if (isLatestNoteFrom(event, currentUserId)) return;

    unseen.push(event.id);
  });
  return unseen;
}

interface UseUnseenEventUpdatesResult {
  unseenEventIds: ReadonlySet<string>;
  markSeen: (eventId: string) => void;
}

export default function useUnseenEventUpdates(
  events: Event[],
  viewKey: string,
  isLoaded: boolean,
  currentUserId?: string
): UseUnseenEventUpdatesResult {
  const stateRef = useRef(createUnseenTrackingState());
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const eventsRef = useRef(events);
  eventsRef.current = events;

  const [unseenEventIds, setUnseenEventIds] =
    useState<ReadonlySet<string>>(EMPTY_IDS);

  const forget = useCallback((eventId: string) => {
    const timer = timersRef.current.get(eventId);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(eventId);
    }
    setUnseenEventIds((prev) => {
      if (!prev.has(eventId)) return prev;
      const next = new Set(prev);
      next.delete(eventId);
      return next;
    });
  }, []);

  useEffect(() => {
    const newlyUnseen = collectUnseenEventIds(
      events,
      stateRef.current,
      viewKey,
      isLoaded,
      currentUserId
    );
    if (newlyUnseen.length === 0) return;

    setUnseenEventIds((prev) => {
      const next = new Set(prev);
      newlyUnseen.forEach((id) => next.add(id));
      return next;
    });

    newlyUnseen.forEach((id) => {
      const existing = timersRef.current.get(id);
      if (existing) clearTimeout(existing);
      timersRef.current.set(
        id,
        setTimeout(() => forget(id), UNSEEN_UPDATE_FADE_MS)
      );
    });
  }, [events, viewKey, isLoaded, currentUserId, forget]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const markSeen = useCallback(
    (eventId: string) => {
      const event = eventsRef.current.find((item) => item.id === eventId);
      if (event) {
        stateRef.current.knownModifiedById.set(eventId, event.modified);
      }
      forget(eventId);
    },
    [forget]
  );

  return { unseenEventIds, markSeen };
}
